import React__default from 'react';
import { throttle } from 'lodash';
import { useGetLatest, getFirstDefined, makePropGetter, useMountedLayoutEffect, passiveEventSupported } from './utils.js';

const actions = {
    init: 'init',
    columnStartResizing: 'columnStartResizing',
    columnResizing: 'columnResizing',
    columnDoneResizing: 'columnDoneResizing',
    resetResize: 'resetResize',
    resizeTable: 'resizeTable',
};
const handler = throttle((dispatch, type, e, columns, rtl) => {
    dispatch({ type, clientX: e.clientX, columns, rtl });
}, 10);
function getLeafHeaders(header) {
    const leafHeaders = [];
    const recurseHeader = (h) => {
        if (h.columns && h.columns.length) {
            h.columns.map(recurseHeader);
        }
        leafHeaders.push(header);
    };
    recurseHeader(header);
    return leafHeaders;
}
const defaultGetResizerProps = (props, { instance, header }) => {
    const { dispatch, visibleColumns } = instance;
    const columns = visibleColumns.filter(c => c.id !== 'row-menu-column');
    const headersToResize = getLeafHeaders(header);
    const headerIdWidths = headersToResize.map(d => [d.id, d.totalWidth]);
    const events = {
        moveEvent: 'mousemove',
        moveHandler: (e) => handler(dispatch, actions.columnResizing, e, columns, instance.rtl),
        upEvent: 'mouseup',
        upHandler: () => {
            document.removeEventListener('mousemove', events.moveHandler);
            document.removeEventListener('mouseup', events.upHandler);
            dispatch({ type: actions.columnDoneResizing });
        },
    };
    const onResizeStart = (event, h) => {
        const passiveIfSupported = passiveEventSupported() ? { passive: false } : false;
        document.addEventListener(events.moveEvent, events.moveHandler, passiveIfSupported);
        document.addEventListener(events.upEvent, events.upHandler, passiveIfSupported);
        dispatch({
            type: actions.columnStartResizing,
            columnId: h.id,
            clientX: event.clientX,
            columnWidth: header.totalWidth,
            headerIdWidths,
        });
    };
    return [
        props,
        {
            onMouseDown: (e) => {
                e.persist();
                onResizeStart(e, header);
                e.preventDefault();
            },
            style: {
                cursor: 'col-resize',
            },
            draggable: false,
            role: 'separator',
        },
    ];
};
const initialState = {
    startX: 0,
    totalWidth: undefined,
    totalHeight: undefined,
    columnWidth: 0,
    columnWidths: {},
};
const parseNumber = (value) => {
    return parseInt(value.toString(), 0);
};
const getNewWidth = (column, actualWidth) => {
    const minWidth = column.minWidth || 0;
    const maxWidth = column.maxWidth || 0;
    let width = actualWidth || 0;
    if (minWidth && width < minWidth) {
        width = minWidth;
    }
    else if (maxWidth && width > maxWidth) {
        width = maxWidth;
    }
    return parseNumber(width);
};
const sizeColumnsToFit = (columns, width, columnWidths, columnResizingShift) => {
    const totalWidth = columnResizingShift ? width - 16 : width;
    const colsToSpread = [];
    const colsToNotSpread = [];
    columns.forEach((column) => {
        if (column.flex) {
            colsToSpread.push(column);
        }
        else {
            colsToNotSpread.push(column);
        }
    });
    const staticWidth = colsToNotSpread.reduce((acc, column) => acc + (column === null || column === void 0 ? void 0 : column.width), 0);
    const allColumnsWidth = columns.reduce((acc, column) => acc + (column === null || column === void 0 ? void 0 : column.width), 0);
    const availableWidth = totalWidth - staticWidth;
    const availableFlexWidth = totalWidth - allColumnsWidth;
    const totalFlex = colsToSpread.reduce((acc, column) => acc + ((column === null || column === void 0 ? void 0 : column.flex) || 0), 0);
    const newColumnWidths = {};
    colsToNotSpread.forEach((column) => {
        newColumnWidths[column.id || 0] = getNewWidth(column, column === null || column === void 0 ? void 0 : column.width);
    });
    colsToSpread.forEach((column) => {
        const width = availableFlexWidth && Object.keys(columnWidths).length
            ? Math.round(columnWidths[column.id || 0] + (availableFlexWidth / totalFlex) * ((column === null || column === void 0 ? void 0 : column.flex) || 1))
            : Math.round((availableWidth / totalFlex) * ((column === null || column === void 0 ? void 0 : column.flex) || 1));
        newColumnWidths[column.id || 0] = getNewWidth(column, width);
    });
    return newColumnWidths;
};
const reducer = (columnResizingShift, columns) => (state = { columnSizing: initialState }, action) => {
    if (action.type === actions.init) {
        return Object.assign(Object.assign({}, state), { columnSizing: Object.assign(Object.assign({}, initialState), state.columnSizing) });
    }
    if (action.type === actions.resizeTable) {
        return Object.assign(Object.assign({}, state), { columnSizing: Object.assign(Object.assign({}, state.columnSizing), { totalWidth: action.size.width, totalHeight: action.size.height, columnWidths: Object.assign(Object.assign({}, state.columnSizing.columnWidths), sizeColumnsToFit(action.columns, action.size.width, state.columnSizing.columnWidths, columnResizingShift)) }) });
    }
    if (action.type === actions.columnStartResizing) {
        const { clientX, columnId, columnWidth, headerIdWidths } = action;
        return Object.assign(Object.assign({}, state), { columnSizing: Object.assign(Object.assign({}, state.columnSizing), { startColumnWidths: state.columnSizing.columnWidths, startX: clientX, columnWidth,
                columnId,
                headerIdWidths }) });
    }
    if (action.type === actions.columnResizing) {
        if (columnResizingShift) {
            const { clientX, rtl } = action;
            const { startX, columnWidth, headerIdWidths } = state.columnSizing;
            const deltaX = rtl ? -(clientX - (startX || 0)) : clientX - (startX || 0);
            const percentageDeltaX = deltaX / columnWidth;
            const newColumnWidths = {};
            headerIdWidths.forEach(([headerId, headerWidth]) => {
                const { maxWidth, minWidth = 50 } = columns.find(({ id }) => headerId === id) || {};
                let sizedColumnWidth = headerWidth + headerWidth * percentageDeltaX;
                sizedColumnWidth = Math.max(sizedColumnWidth, minWidth);
                if (maxWidth) {
                    sizedColumnWidth = Math.min(sizedColumnWidth, maxWidth);
                }
                newColumnWidths[headerId] = sizedColumnWidth;
            });
            return Object.assign(Object.assign({}, state), { columnSizing: Object.assign(Object.assign({}, state.columnSizing), { columnWidths: Object.assign(Object.assign({}, state.columnSizing.columnWidths), newColumnWidths) }) });
        }
        else {
            const { clientX, columns, rtl } = action;
            const { startX, columnId, startColumnWidths } = state.columnSizing;
            const deltaX = rtl ? -(clientX - (startX || 0)) : clientX - (startX || 0);
            const newColumnWidths = {};
            const columnIndex = columns.findIndex((c) => c.id === columnId);
            const columnsIdsToSpread = columns.slice(columnIndex + 1).map((c) => c.id);
            let temp = columnsIdsToSpread;
            let pixels = 0;
            if (columnsIdsToSpread.length > 0) {
                Object.keys(startColumnWidths).forEach((key) => {
                    const headerWidth = startColumnWidths[key];
                    const column = columns.find(({ id }) => id === key);
                    if (!column) {
                        return;
                    }
                    const { maxWidth, minWidth = 50 } = column;
                    if (columnId === key) {
                        const newCurrentColumnWidth = parseNumber(Math.max(headerWidth + deltaX, 0));
                        if (maxWidth && newCurrentColumnWidth >= maxWidth) {
                            newColumnWidths[key] = maxWidth;
                        }
                        else if (minWidth && newCurrentColumnWidth <= minWidth) {
                            newColumnWidths[key] = minWidth;
                        }
                        else {
                            newColumnWidths[key] = newCurrentColumnWidth;
                        }
                    }
                    else {
                        if (columnsIdsToSpread.includes(key)) {
                            const newColumnWidth = parseNumber(Math.max(headerWidth + -deltaX / (temp.length || 1), 0));
                            if (maxWidth && newColumnWidth >= maxWidth) {
                                pixels -= maxWidth - headerWidth;
                                temp = temp.filter((c) => c === key);
                                newColumnWidths[key] = maxWidth;
                            }
                            else if (minWidth && newColumnWidth <= minWidth) {
                                pixels += headerWidth - minWidth;
                                temp = temp.filter((c) => c === key);
                                newColumnWidths[key] = minWidth;
                            }
                            else {
                                newColumnWidths[key] = parseNumber(Math.max(headerWidth + pixels + -deltaX / (temp.length || 1), 0));
                            }
                        }
                        else {
                            newColumnWidths[key] = headerWidth;
                        }
                    }
                });
            }
            return Object.assign(Object.assign({}, state), { columnSizing: Object.assign(Object.assign({}, state.columnSizing), { columnWidths: Object.assign(Object.assign({}, state.columnSizing.columnWidths), newColumnWidths) }) });
        }
    }
    if (action.type === actions.columnDoneResizing) {
        return Object.assign(Object.assign({}, state), { columnSizing: Object.assign(Object.assign({}, state.columnSizing), { startX: undefined, columnId: undefined }) });
    }
    return state;
};
const useInstanceBeforeDimensions = (instance) => {
    const { flatHeaders, getHooks, state, dataTableOptions: { columnResizingShift, disableResizing }, } = instance;
    const { columnId, totalWidth, columnWidths } = state.columnSizing;
    const getInstance = useGetLatest(instance);
    if (!totalWidth) {
        return;
    }
    flatHeaders.forEach(header => {
        const canResize = getFirstDefined(header.disableResizing === true ? false : undefined, disableResizing === true ? false : undefined, true);
        header.canResize = canResize;
        header.width = columnWidths[header.id];
        if (columnResizingShift) {
            header.minWidth = columnWidths[header.id];
            header.maxWidth = columnWidths[header.id];
        }
        header.isResizing = columnId === header.id;
        if (canResize) {
            header.getResizerProps = makePropGetter(getHooks().getResizerProps, {
                instance: getInstance(),
                header,
            });
        }
    });
};
const useInstance = (instance) => {
    const { dispatch, autoResetResize = true, visibleColumns } = instance;
    const columns = visibleColumns.filter(c => c.id !== 'row-menu-column');
    const getAutoResetResize = useGetLatest(autoResetResize);
    useMountedLayoutEffect(() => {
        if (getAutoResetResize()) {
            dispatch({ type: actions.resetResize, columns });
        }
    }, [columns]);
    const resetResizing = React__default.useCallback(() => dispatch({ type: actions.resetResize, columns }), [columns, dispatch]);
    const resizeTable = React__default.useCallback((size) => dispatch({ type: actions.resizeTable, size, columns }), [columns, dispatch]);
    Object.assign(instance, {
        resetResizing,
        resizeTable,
    });
};
const useColumnResize = (hooks) => {
    const { dataTableOptions: { columnResizingShift, columns }, } = hooks;
    hooks.getResizerProps = [defaultGetResizerProps];
    hooks.getHeaderProps.push({
        style: {
            position: 'relative',
        },
    });
    hooks.stateReducers.push(reducer(columnResizingShift, columns));
    hooks.useInstance.push(useInstance);
    hooks.useInstanceBeforeDimensions.push(useInstanceBeforeDimensions);
};

export { useColumnResize as default };
//# sourceMappingURL=useColumnResize.js.map
